import { Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "@/components/toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { PartyPc } from "@/lib/core/schemas/party-view";
import { callMcpTool } from "@/src/api/mcp";

const KIND_SEP_RE = /[-_\s]+/;

function titleCase(kind: string): string {
  return kind
    .split(KIND_SEP_RE)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function EquipmentPanel({
  pc,
  campaignId,
  onChange,
}: {
  pc: PartyPc | null;
  campaignId: string;
  onChange: () => unknown;
}) {
  const [busyId, setBusyId] = useState<string | null>(null);

  if (!pc) {
    return (
      <div className="py-8 text-center text-muted-foreground text-sm">
        No character selected.
      </div>
    );
  }

  const actionNames = new Map(
    (pc.availableActions ?? []).map((a) => [a.id, a.name])
  );

  const runTool = async (
    toolName: "equip_item" | "unequip_item",
    itemId: string,
    failVerb: string
  ) => {
    setBusyId(itemId);
    try {
      await callMcpTool(toolName, { actorId: pc.id, itemId }, campaignId);
      await onChange();
    } catch (err) {
      toast({
        type: "error",
        description: err instanceof Error ? err.message : `${failVerb} failed`,
      });
    } finally {
      setBusyId(null);
    }
  };

  // Equipped items live in the slots section (with Unequip); the inventory
  // section shows the remaining (un-equipped) items, classified by
  // equippability — never hidden (LAF-330 "classify don't filter").
  const unequipped = pc.inventory.filter((i) => !i.equipped);

  return (
    <div className="space-y-4">
      {pc.slots.length > 0 && (
        <div className="space-y-3">
          {pc.slots.map((slot) => (
            <Card className="border-border bg-card" key={slot.kind}>
              <CardHeader className="p-4 pb-2">
                <CardTitle className="flex items-center justify-between text-base text-foreground">
                  <span>{titleCase(slot.kind)}</span>
                  <span className="font-normal text-muted-foreground text-sm">
                    {slot.occupancy}/{slot.capacity}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                {slot.items.length === 0 ? (
                  <p className="text-muted-foreground text-sm">
                    Empty — {slot.free} {slot.free === 1 ? "slot" : "slots"}{" "}
                    open.
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {slot.items.map((item) => (
                      <li
                        className="flex items-start justify-between gap-2"
                        key={item.id}
                      >
                        <div className="min-w-0 space-y-1.5">
                          <div className="font-medium text-foreground text-sm">
                            {item.name}
                          </div>
                          {item.grantedActionIds.length > 0 ? (
                            <div className="flex flex-wrap gap-1.5">
                              {item.grantedActionIds.map((actionId) => (
                                <Badge
                                  className="text-[10px]"
                                  key={actionId}
                                  variant="secondary"
                                >
                                  {actionNames.get(actionId) ?? actionId}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-xs">
                              No granted actions
                            </span>
                          )}
                        </div>
                        <Button
                          className="shrink-0"
                          disabled={busyId !== null}
                          onClick={() =>
                            runTool("unequip_item", item.id, "Unequip")
                          }
                          size="sm"
                          type="button"
                          variant="outline"
                        >
                          {busyId === item.id && (
                            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                          )}
                          Unequip
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Card className="border-border bg-card">
        <CardHeader className="p-4 pb-2">
          <CardTitle className="text-base text-foreground">Inventory</CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          {unequipped.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              No unequipped items.
            </p>
          ) : (
            <ul className="space-y-2">
              {unequipped.map((item) => (
                <li
                  className="flex items-center justify-between gap-2"
                  key={item.id}
                >
                  <span className="min-w-0 truncate text-foreground text-sm">
                    {item.name}
                  </span>
                  {item.slotKind === null ? null : item.canEquip ? (
                    <Button
                      className="shrink-0"
                      disabled={busyId !== null}
                      onClick={() => runTool("equip_item", item.id, "Equip")}
                      size="sm"
                      type="button"
                      variant="outline"
                    >
                      {busyId === item.id && (
                        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                      )}
                      Equip
                    </Button>
                  ) : (
                    <span className="shrink-0 text-muted-foreground text-xs">
                      {titleCase(item.slotKind)} slot full
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
